import { useQueries } from "@tanstack/react-query";
import { useState } from "react";
import { NEWS_CATEGORIES, NEWS_SOURCES, sourceById, type NewsItem, type NewsSourcePayload } from "../../../shared/news";
import { api } from "../../data/api";
import { readSnapshot } from "../../data/persist";
import { relativeAge } from "../../ui/time";
import "./news.css";

const MIN = 60_000;

/** Several sources at once, sharing the cache (and snapshots) with useNewsSource. */
export function useNewsSources(ids: string[]) {
  return useQueries({
    queries: ids.map((id) => {
      const key = ["news", "source", id] as const;
      const snap = () => readSnapshot<NewsSourcePayload>(JSON.stringify(key));
      return {
        queryKey: key,
        queryFn: ({ signal }: { signal: AbortSignal }) => api<NewsSourcePayload>(`/news/source/${encodeURIComponent(id)}`, { signal }),
        initialData: () => snap()?.data,
        initialDataUpdatedAt: () => snap()?.savedAt,
        staleTime: 10 * MIN,
        refetchInterval: 15 * MIN,
      };
    }),
  });
}

/** Newest first, one copy of a story that several sources carry. */
export function mergeStories(lists: (NewsItem[] | undefined)[], max: number): NewsItem[] {
  const seen = new Set<string>();
  return lists
    .flatMap((l) => l ?? [])
    .sort((a, b) => (b.publishedAt ?? "").localeCompare(a.publishedAt ?? ""))
    .filter((it) => {
      const k = it.title.toLowerCase().replace(/\W+/g, " ").trim().slice(0, 60);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .slice(0, max);
}

/** Take turns between sources: the top story from each, then the second… */
export function interleave(lists: NewsItem[][], max: number): NewsItem[] {
  const out: NewsItem[] = [];
  for (let i = 0; out.length < max && lists.some((l) => l[i]); i++) for (const l of lists) if (l[i] && out.length < max) out.push(l[i]!);
  return out;
}

export function SourceBadge({ id }: { id: string }) {
  const s = sourceById(id);
  return (
    <span className="news-badge" style={{ ["--src" as string]: s?.color ?? "var(--accent)" }}>
      {s?.name ?? (id === "espn" ? "ESPN" : id)}
    </span>
  );
}

export function storyAge(it: NewsItem): string | null {
  return it.publishedAt ? relativeAge(it.publishedAt) : null;
}

/** A remote picture that quietly disappears if it fails to load. */
export function NewsImage({ src, className, alt = "" }: { src: string | null; className?: string; alt?: string }) {
  const [failed, setFailed] = useState<string | null>(null);
  if (!src || failed === src) return null;
  return <img className={className} src={src} alt={alt} loading="lazy" decoding="async" referrerPolicy="no-referrer" onError={() => setFailed(src)} />;
}

/** Which sources a list widget shows: all turned on, one category, or one source. */
export function sourcesFor(filter: string, enabled: string[]): string[] {
  if (filter.startsWith("source:")) return [filter.slice(7)];
  if (filter === "all" || !filter) return enabled;
  return enabled.filter((id) => sourceById(id)?.category === filter);
}

export function filterLabel(filter: string): string {
  if (filter.startsWith("source:")) return sourceById(filter.slice(7))?.name ?? "Headlines";
  return NEWS_CATEGORIES.find((c) => c.key === filter)?.label ?? "Headlines";
}

export const FILTER_OPTIONS = [
  { value: "all", label: "All your sources" },
  ...NEWS_CATEGORIES.map((c) => ({ value: c.key, label: `${c.label} (your sources)` })),
  ...NEWS_SOURCES.map((s) => ({ value: `source:${s.id}`, label: `Only ${s.name}` })),
];
